import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { supabase, isSupabaseConfigured } from "../lib/supabase";

export default function AdminInvoiceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const invoiceRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [invoice, setInvoice] = useState(null);
    const [items, setItems] = useState([]);
    const [message, setMessage] = useState("");

    useEffect(() => {
        loadInvoice();
    }, [id]);

    async function loadInvoice() {
        try {
            setLoading(true);
            setMessage("");

            if (!isSupabaseConfigured || !supabase) {
                setMessage("Supabase n'est pas configuré.");
                return;
            }

            const { data: invoiceData, error: invoiceError } = await supabase
                .from("invoices")
                .select("*")
                .eq("id", id)
                .single();

            if (invoiceError) throw invoiceError;

            const { data: itemsData, error: itemsError } = await supabase
                .from("invoice_items")
                .select("*")
                .eq("invoice_id", id)
                .order("created_at", { ascending: true });

            if (itemsError) throw itemsError;

            setInvoice(invoiceData);
            setItems(itemsData || []);
        } catch (error) {
            console.error(error);
            setMessage("Impossible de charger cette facture.");
        } finally {
            setLoading(false);
        }
    }

    function addDays(dateString, days) {
        if (!dateString) return null;

        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return null;

        date.setDate(date.getDate() + days);
        return date;
    }

    function formatDate(date) {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    }

    function formatMoney(value, currency = "EUR") {
        const numericValue = Number(value || 0);
        return new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency,
        }).format(numericValue);
    }

    function safeText(value) {
        return value || "-";
    }

    const computedDueDate = useMemo(() => {
        if (invoice?.due_date) return invoice.due_date;

        const autoDate = addDays(invoice?.issue_date, 14);
        return autoDate ? autoDate.toISOString().slice(0, 10) : null;
    }, [invoice]);

    const groupedSender = useMemo(() => {
        if (!invoice) return [];
        return [
            invoice.sender_name,
            invoice.sender_address,
            invoice.sender_email,
            invoice.sender_phone,
        ].filter(Boolean);
    }, [invoice]);

    const groupedRecipient = useMemo(() => {
        if (!invoice) return [];
        return [
            invoice.recipient_name,
            invoice.recipient_address,
            invoice.recipient_email,
        ].filter(Boolean);
    }, [invoice]);

    async function downloadPdf() {
        if (!invoiceRef.current || !invoice) return;

        try {
            setDownloading(true);
            setMessage("");

            const canvas = await html2canvas(invoiceRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#f5f7fb",
                logging: false,
            });

            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF("p", "mm", "a4");
            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 6;

            const usableWidth = pageWidth - margin * 2;
            const usableHeight = pageHeight - margin * 2;

            const imgWidth = usableWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const ratio = Math.min(usableWidth / imgWidth, usableHeight / imgHeight);
            const finalWidth = imgWidth * ratio;
            const finalHeight = imgHeight * ratio;

            const x = (pageWidth - finalWidth) / 2;
            const y = margin;

            pdf.addImage(imgData, "PNG", x, y, finalWidth, finalHeight);
            pdf.save(`${invoice.invoice_number || "facture"}.pdf`);
        } catch (error) {
            console.error(error);
            setMessage("Impossible de générer le PDF.");
        } finally {
            setDownloading(false);
        }
    }

    if (loading) {
        return (
            <div style={styles.screen}>
                <div style={styles.wrapper}>
                    <div style={styles.toolbar}>
                        <button style={styles.secondaryBtn} onClick={() => navigate(-1)}>
                            Retour
                        </button>
                    </div>
                    <div style={styles.stateCard}>Chargement de la facture...</div>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div style={styles.screen}>
                <div style={styles.wrapper}>
                    <div style={styles.toolbar}>
                        <button style={styles.secondaryBtn} onClick={() => navigate(-1)}>
                            Retour
                        </button>
                    </div>
                    <div style={styles.stateCard}>{message || "Facture introuvable."}</div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.screen}>
            <div style={styles.wrapper}>
                <div style={styles.toolbar}>
                    <div style={styles.toolbarLeft}>
                        <button style={styles.secondaryBtn} onClick={() => navigate(-1)}>
                            Retour
                        </button>
                        <span style={styles.invoiceChip}>{invoice.invoice_number}</span>
                    </div>

                    <div style={styles.toolbarRight}>
                        <button
                            style={styles.primaryBtn}
                            onClick={downloadPdf}
                            disabled={downloading}
                        >
                            {downloading ? "Téléchargement..." : "Télécharger la facture"}
                        </button>
                    </div>
                </div>

                {message ? <div style={styles.errorBox}>{message}</div> : null}

                <div ref={invoiceRef} style={styles.invoicePage}>
                    <div style={styles.headerGradient}>
                        <div style={styles.headerContent}>
                            <div>
                                <div style={styles.headerKicker}>Facture</div>
                                <h1 style={styles.headerTitle}>FACTURE</h1>

                                <div style={styles.metaGrid}>
                                    <div style={styles.metaRow}>
                                        <span style={styles.metaLabel}>Numéro</span>
                                        <span style={styles.metaValue}>{safeText(invoice.invoice_number)}</span>
                                    </div>
                                    <div style={styles.metaRow}>
                                        <span style={styles.metaLabel}>Émise le</span>
                                        <span style={styles.metaValue}>{formatDate(invoice.issue_date)}</span>
                                    </div>
                                    <div style={styles.metaRow}>
                                        <span style={styles.metaLabel}>Échéance</span>
                                        <span style={styles.metaValue}>{formatDate(computedDueDate)}</span>
                                    </div>
                                </div>
                            </div>
                            <div
                                style={{
                                    background: "rgba(255,255,255,0.1)",
                                    padding: "10px 14px",
                                    borderRadius: "12px"
                                }}
                            >
                                <img
                                    src="/images/logo-weblax.png"
                                    alt="Weblax"
                                    style={{ height: "50px" }}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={styles.body}>
                        <div style={styles.infoCards}>
                            <div style={styles.infoCard}>
                                <div style={styles.cardTitle}>Émetteur</div>
                                {groupedSender.map((line, index) => (
                                    <div key={index} style={index === 0 ? styles.mainLine : styles.subLine}>
                                        {line}
                                    </div>
                                ))}
                            </div>

                            <div style={styles.infoCard}>
                                <div style={styles.cardTitle}>Facturer à</div>
                                {groupedRecipient.map((line, index) => (
                                    <div key={index} style={index === 0 ? styles.mainLine : styles.subLine}>
                                        {line}
                                    </div>
                                ))}
                            </div>

                            <div style={styles.infoCard}>
                                <div style={styles.cardTitle}>Paiement</div>
                                <div style={styles.mainLine}>Virement bancaire</div>
                                <div style={styles.subLine}>
                                    Merci d’indiquer la référence :
                                    <br />
                                    <strong>{safeText(invoice.invoice_number)}</strong>
                                </div>
                            </div>
                        </div>

                        <div style={styles.amountBanner}>
                            <div style={styles.amountValue}>
                                {formatMoney(invoice.total_amount, invoice.currency)}
                            </div>
                            <div style={styles.amountText}>
                                dus le {formatDate(computedDueDate)}
                            </div>
                        </div>

                        <div style={styles.bankBox}>
                            <div style={styles.sectionTitle}>Coordonnées bancaires</div>
                            <div style={styles.bankGrid}>
                                <div>
                                    <span style={styles.bankLabel}>Bénéficiaire</span>
                                    <div style={styles.bankValue}>
                                        {safeText(invoice.beneficiary_name_snapshot)}
                                    </div>
                                </div>

                                <div>
                                    <span style={styles.bankLabel}>IBAN</span>
                                    <div style={styles.bankValue}>{safeText(invoice.iban_snapshot)}</div>
                                </div>

                                <div>
                                    <span style={styles.bankLabel}>BIC</span>
                                    <div style={styles.bankValue}>{safeText(invoice.bic_snapshot)}</div>
                                </div>

                                <div>
                                    <span style={styles.bankLabel}>Banque</span>
                                    <div style={styles.bankValue}>
                                        {safeText(invoice.bank_name_snapshot)}
                                    </div>
                                </div>
                            </div>

                            {invoice.payment_note_snapshot ? (
                                <div style={styles.paymentNote}>{invoice.payment_note_snapshot}</div>
                            ) : null}
                        </div>

                        <div style={styles.tableWrap}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ ...styles.th, textAlign: "left" }}>Description</th>
                                        <th style={styles.th}>Qté</th>
                                        <th style={styles.th}>Prix unitaire</th>
                                        <th style={styles.th}>Taxe</th>
                                        <th style={styles.th}>Montant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={item.id}>
                                            <td style={{ ...styles.td, textAlign: "left" }}>
                                                <div style={styles.itemName}>{item.product_name}</div>
                                                {item.description ? (
                                                    <div style={styles.itemDescription}>{item.description}</div>
                                                ) : null}
                                            </td>
                                            <td style={styles.td}>{Number(item.quantity || 0)}</td>
                                            <td style={styles.td}>
                                                {formatMoney(item.unit_price, invoice.currency)}
                                            </td>
                                            <td style={styles.td}>{Number(item.tax_rate || 0)} %</td>
                                            <td style={styles.td}>
                                                {formatMoney(item.line_total, invoice.currency)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={styles.summaryRowWrap}>
                            <div style={styles.summarySpacer} />
                            <div style={styles.summaryCard}>
                                <div style={styles.summaryRow}>
                                    <span>Sous-total</span>
                                    <strong>{formatMoney(invoice.subtotal, invoice.currency)}</strong>
                                </div>
                                <div style={styles.summaryRow}>
                                    <span>TVA</span>
                                    <strong>{formatMoney(invoice.tax_amount, invoice.currency)}</strong>
                                </div>
                                <div style={styles.summaryTotal}>
                                    <span>Total</span>
                                    <strong>{formatMoney(invoice.total_amount, invoice.currency)}</strong>
                                </div>
                            </div>
                        </div>

                        {invoice.notes ? (
                            <div style={styles.notesBox}>
                                <div style={styles.sectionTitle}>Notes</div>
                                <div style={styles.noteText}>{invoice.notes}</div>
                            </div>
                        ) : null}

                        <div style={styles.footerBar}>
                            <div>{safeText(invoice.sender_name)}</div>
                            <div>{safeText(invoice.sender_phone)}</div>
                            <div>{safeText(invoice.sender_email)}</div>
                        </div>

                        <div style={styles.pageFooter}>
                            <span>{safeText(invoice.invoice_number)}</span>
                            <span>Page 1 sur 1</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    screen: {
        minHeight: "100vh",
        background: "#0b1020",
        padding: "32px 16px",
    },
    wrapper: {
        maxWidth: "1200px",
        margin: "0 auto",
    },
    toolbar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
        marginBottom: "18px",
        flexWrap: "wrap",
    },
    toolbarLeft: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
    },
    toolbarRight: {
        display: "flex",
        gap: "10px",
    },
    primaryBtn: {
        background: "linear-gradient(135deg, #4454ff 0%, #7a49ff 55%, #f0c15b 100%)",
        color: "#fff",
        border: "none",
        padding: "12px 18px",
        borderRadius: "12px",
        cursor: "pointer",
        fontWeight: 700,
        boxShadow: "0 10px 24px rgba(82, 76, 255, 0.25)",
    },
    secondaryBtn: {
        background: "#151c34",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.1)",
        padding: "12px 18px",
        borderRadius: "12px",
        cursor: "pointer",
        fontWeight: 600,
    },
    invoiceChip: {
        padding: "8px 12px",
        borderRadius: "999px",
        background: "rgba(255,255,255,0.08)",
        color: "#fff",
        fontSize: "0.9rem",
    },
    errorBox: {
        background: "rgba(255,87,87,0.12)",
        border: "1px solid rgba(255,87,87,0.3)",
        color: "#fff",
        padding: "12px 14px",
        borderRadius: "12px",
        marginBottom: "14px",
    },
    stateCard: {
        background: "#121933",
        color: "#fff",
        padding: "24px",
        borderRadius: "18px",
        border: "1px solid rgba(255,255,255,0.08)",
    },
    invoicePage: {
        width: "794px",
        minHeight: "1123px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f5f7fb",
        borderRadius: "22px",
        overflow: "hidden",
        boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
        display: "flex",
        flexDirection: "column",
    },
    headerGradient: {
        background:
            "linear-gradient(135deg, #08143d 0%, #102766 42%, #6c42ff 76%, #f0c15b 100%)",
        padding: "34px 38px 30px",
        color: "#fff",
    },
    headerContent: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "20px",
        flexWrap: "wrap",
    },
    headerKicker: {
        textTransform: "uppercase",
        letterSpacing: "0.2em",
        fontSize: "0.78rem",
        opacity: 0.85,
        marginBottom: "8px",
    },
    headerTitle: {
        fontSize: "2.1rem",
        margin: "0 0 18px",
        letterSpacing: "0.04em",
    },
    metaGrid: {
        display: "grid",
        gap: "8px",
    },
    metaRow: {
        display: "flex",
        gap: "14px",
        flexWrap: "wrap",
    },
    metaLabel: {
        minWidth: "86px",
        opacity: 0.8,
    },
    metaValue: {
        fontWeight: 700,
    },
    brandBlock: {
        display: "flex",
        alignItems: "center",
        gap: "14px",
    },
    brandSymbol: {
        width: "56px",
        height: "56px",
        borderRadius: "18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.8rem",
        background: "linear-gradient(135deg, #7a49ff 0%, #f0c15b 100%)",
        color: "#fff",
        boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
    },
    brandTitle: {
        fontSize: "1.5rem",
        fontWeight: 800,
        letterSpacing: "0.08em",
    },
    brandSubtitle: {
        fontSize: "0.95rem",
        opacity: 0.9,
        letterSpacing: "0.2em",
    },
    body: {
        padding: "28px 34px 28px",
        display: "flex",
        flexDirection: "column",
        flex: 1,
    },
    infoCards: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "16px",
        marginBottom: "18px",
    },
    infoCard: {
        background: "#ffffff",
        border: "1px solid #e4e8f4",
        borderRadius: "18px",
        padding: "18px",
        minHeight: "146px",
    },
    cardTitle: {
        color: "#15234e",
        fontWeight: 800,
        fontSize: "1rem",
        marginBottom: "12px",
    },
    mainLine: {
        color: "#0c1635",
        fontWeight: 700,
        marginBottom: "6px",
    },
    subLine: {
        color: "#35415f",
        lineHeight: 1.5,
        marginBottom: "4px",
        whiteSpace: "pre-line",
    },
    amountBanner: {
        display: "inline-flex",
        alignItems: "center",
        gap: "14px",
        background: "#08143d",
        color: "#fff",
        borderRadius: "16px",
        padding: "16px 20px",
        marginBottom: "20px",
        boxShadow: "0 12px 30px rgba(8,20,61,0.12)",
    },
    amountValue: {
        fontSize: "2rem",
        fontWeight: 900,
        lineHeight: 1,
    },
    amountText: {
        fontSize: "1.02rem",
        opacity: 0.95,
    },
    bankBox: {
        background: "#ffffff",
        border: "1px solid #e4e8f4",
        borderLeft: "5px solid #4454ff",
        borderRadius: "16px",
        padding: "16px",
        marginBottom: "18px",
    },
    sectionTitle: {
        color: "#15234e",
        fontWeight: 800,
        marginBottom: "12px",
        fontSize: "1rem",
    },
    bankGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "14px 22px",
    },
    bankLabel: {
        display: "block",
        fontSize: "0.84rem",
        color: "#6a7698",
        marginBottom: "4px",
    },
    bankValue: {
        color: "#0c1635",
        fontWeight: 700,
        wordBreak: "break-word",
    },
    paymentNote: {
        marginTop: "14px",
        color: "#44506f",
        lineHeight: 1.5,
    },
    tableWrap: {
        background: "#ffffff",
        borderRadius: "18px",
        overflow: "hidden",
        border: "1px solid #e4e8f4",
        marginBottom: "18px",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    th: {
        background: "#08143d",
        color: "#fff",
        padding: "16px 14px",
        fontSize: "0.92rem",
        textAlign: "center",
    },
    td: {
        padding: "16px 14px",
        borderBottom: "1px solid #edf0f8",
        color: "#1f2a4b",
        textAlign: "center",
        verticalAlign: "top",
    },
    itemName: {
        fontWeight: 800,
        color: "#0c1635",
        marginBottom: "4px",
    },
    itemDescription: {
        color: "#67728f",
        lineHeight: 1.5,
    },
    summaryRowWrap: {
        display: "grid",
        gridTemplateColumns: "1fr 320px",
        gap: "20px",
        marginBottom: "18px",
    },
    summarySpacer: {},
    summaryCard: {
        background: "#ffffff",
        border: "1px solid #e4e8f4",
        borderRadius: "18px",
        overflow: "hidden",
    },
    summaryRow: {
        display: "flex",
        justifyContent: "space-between",
        padding: "14px 18px",
        borderBottom: "1px solid #edf0f8",
        color: "#2b3656",
    },
    summaryTotal: {
        display: "flex",
        justifyContent: "space-between",
        padding: "16px 18px",
        background: "linear-gradient(135deg, #4454ff 0%, #7a49ff 100%)",
        color: "#fff",
        fontSize: "1.05rem",
        fontWeight: 800,
    },
    notesBox: {
        background: "#eef2fb",
        border: "1px solid #dde5fb",
        borderRadius: "16px",
        padding: "18px",
        marginBottom: "22px",
    },
    noteText: {
        color: "#394566",
        lineHeight: 1.6,
        whiteSpace: "pre-line",
    },
    footerBar: {
        background: "#08143d",
        color: "#fff",
        borderRadius: "14px",
        padding: "14px 18px",
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "16px",
        fontSize: "0.95rem",
        marginBottom: "18px",
    },
    pageFooter: {
        display: "flex",
        justifyContent: "space-between",
        color: "#6b7693",
        fontSize: "0.88rem",
        paddingBottom: "6px",
    },
};